package com.arena_web.ui.sections.profile

import androidx.compose.runtime.*
import com.arena_web.data.dto.AwardCategories
import com.arena_web.data.dto.UserAwardDto
import com.arena_web.ui.localization.AppStrings
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

// Backend seed sport ids (V7 migration) — award terms differ per sport
// ("top scorer" in football vs "most winners" in tennis), while the artwork
// itself already carries the sport-specific design. Mirrors the mobile app's
// AwardsSection.kt.
private const val SPORT_FOOTBALL = "10000000-0000-0000-0000-000000000001"
private const val SPORT_BASKETBALL = "10000000-0000-0000-0000-000000000002"
private const val SPORT_TENNIS = "10000000-0000-0000-0000-000000000003"
private const val SPORT_VOLLEYBALL = "10000000-0000-0000-0000-000000000004"
private const val SPORT_PADEL = "10000000-0000-0000-0000-000000000005"

/** "Top Scorer — 1st place" / "Welcome Award" — localized display title. */
fun awardTitle(t: AppStrings, award: UserAwardDto): String {
    if (award.category == AwardCategories.WELCOME) return t.awardWelcomeTitle
    val term = if (award.category == AwardCategories.ASSIST) {
        when (award.sportId) {
            SPORT_FOOTBALL -> t.awardTermAssistFootball
            SPORT_BASKETBALL -> t.awardTermAssistBasketball
            SPORT_TENNIS -> t.awardTermAssistTennis
            SPORT_VOLLEYBALL -> t.awardTermAssistVolleyball
            SPORT_PADEL -> t.awardTermAssistPadel
            else -> t.awardTermAssistGeneric
        }
    } else {
        when (award.sportId) {
            SPORT_FOOTBALL -> t.awardTermScorerFootball
            SPORT_BASKETBALL -> t.awardTermScorerBasketball
            SPORT_TENNIS -> t.awardTermScorerTennis
            SPORT_VOLLEYBALL -> t.awardTermScorerVolleyball
            SPORT_PADEL -> t.awardTermScorerPadel
            else -> t.awardTermScorerGeneric
        }
    }
    val rank = when (award.rank) {
        1 -> t.awardRank1
        2 -> t.awardRank2
        3 -> t.awardRank3
        else -> null
    }
    return if (rank != null) "$term — $rank" else term
}

/**
 * Square award artwork that flips in place — same perspective /
 * preserve-3d / backface-hidden recipe as the player card's FlippableCard
 * (OverviewSidebar), sized by its grid cell via aspect-ratio 1/1.
 */
@Composable
fun AwardFlipTile(
    award: UserAwardDto,
    isFlipped: Boolean,
    onClick: () -> Unit,
) {
    Div(attrs = {
        style {
            property("position", "relative")
            property("width", "100%")
            property("aspect-ratio", "1 / 1")
            property("perspective", "800px")
            property("cursor", "pointer")
        }
        onClick { onClick() }
    }) {
        Div(attrs = {
            style {
                property("position", "relative")
                property("width", "100%")
                property("height", "100%")
                property("transform-style", "preserve-3d")
                property("transition", "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)")
                property("transform", if (isFlipped) "rotateY(180deg)" else "rotateY(0deg)")
            }
        }) {
            listOf(award.imageUrl to false, award.backImageUrl to true).forEach { (src, isBack) ->
                Img(src = src, attrs = {
                    style {
                        property("position", "absolute")
                        property("inset", "0")
                        property("width", "100%")
                        property("height", "100%")
                        property("object-fit", "contain")
                        property("backface-visibility", "hidden")
                        property("-webkit-backface-visibility", "hidden")
                        if (isBack) property("transform", "rotateY(180deg)")
                    }
                })
            }
        }
    }
}

/**
 * Awards grid — 3 columns of earned-award art, newest first from the API.
 * Clicking a tile flips it in place with the player card's 3D motion to
 * reveal the back face; one award is flipped at a time. Used by the desktop
 * AchievementsTabBlock, the mobile Awards tab and PersonDetailDialog.
 */
@Composable
fun AwardsGridContent(
    awards: List<UserAwardDto>,
    modifier: Modifier = Modifier,
) {
    val t = LocalAppStrings.current

    if (awards.isEmpty()) {
        Box(
            modifier = modifier
                .fillMaxWidth()
                .styleModifier {
                    property("flex", "1 0 0")
                    property("min-height", "120px")
                },
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.profileAwardsEmpty,
                modifier = Modifier.fontSize(14.px).color(AppColors.Text.Muted)
            )
        }
        return
    }

    var flippedId by remember { mutableStateOf<String?>(null) }

    Div(attrs = {
        style {
            property("display", "grid")
            property("grid-template-columns", "repeat(3, 1fr)")
            property("gap", "12px")
            property("width", "100%")
            property("flex", "1 0 0")
            property("min-height", "0")
            property("overflow-y", "auto")
            property("align-content", "start")
            property("scrollbar-width", "thin")
            property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
        }
    }) {
        awards.forEach { award ->
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .gap(6.px)
                    .cursor(Cursor.Pointer),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                AwardFlipTile(
                    award = award,
                    isFlipped = flippedId == award.id,
                    onClick = { flippedId = if (flippedId == award.id) null else award.id },
                )
            }
        }
    }
}
